package de.oevbs.kunde.kundenportal2.components

import dev.fritz2.core.*
import kotlinx.coroutines.flow.map
import org.w3c.dom.HTMLDivElement

/**
 * Diese Komponente stellt ein Eingabefeld für einen sechs-stelligen Code zur Verfügung.
 *
 * Dies wird z.B. bei der Registrierung benötigt, wo der Kunde den Code aus einer E-Mail zur Verifikation hier
 * wieder eingeben muss.
 *
 * Beispiel:
 * ```kotlin
 * val storedCode = storeOf("ABC123", id = "my-code")
 * inputCode(storedCode)
 * ```
 *
 * Bitte beachten: Die Komponente wandelt alle Eingaben in Großbuchstaben um. Auch bei der Eingabe werden per CSS nur
 * Großbuchstaben angezeigt. Aber wenn der Store bereits mit einem String mit Kleinbuchstaben vorbelegt ist, so wird
 * der Inhalt erst nach einer Änderung der Eingabe gewandelt. Ohne Änderung bleiben die Kleinbuchstaben vorhanden.
 *
 * Bei einer Vorbelegung empfiehlt es sich daher, diese vorab in Großbuchstaben zu wandeln!
 *
 * @param value ein [Store] von [String], in dem die Eingabe abgelegt wird.
 * @param id die ID der Komponente. Wenn keine angegeben wird, so wird die ID aus dem [Store] des [value] parameters
 * verwendet.
 */
fun RenderContext.inputCode(
    value: Store<String>,
    id: String? = null
): Tag<HTMLDivElement> {
    val componentId = id ?: value.id
    val inputId = "$componentId-input"
    return div("w-full mb-3 flex justify-center", id = componentId) {
        label("sr-only") {
            +"Code Eingabe"
            `for`(inputId)
        }
        input(
            joinClasses(
                "codeInputUnderline",
                "mb-4 ml-[1ch] pb-2 md:pb-0 font-mono uppercase h-10 md:h-20 text-[2rem] md:text-[4rem]",
                "text-gray-700 tracking-[1ch] w-[13ch] pl-[0.25ch] pt-4",
                "focus-visible:outline-none focus-visible:text-gray-900",
                "border-none focus:ring-0"
            ),
            id = inputId
        ) {
            // Der Hintergrund muss 1 Char kleiner sein als das Input, da der Cursor hinter dem
            // letzten Element Platz einnimmt und das Input "wackeln" würde, wenn die Größe nur
            // an der Anzahl Chars orientiert wäre (Input 13, BG 12)
            inlineStyle("""
                background: repeating-linear-gradient(
                    90deg,
                    /* gray-300 */ #E0E0E0 0,
                    /* gray-300 */ #E0E0E0 1.5ch,
                    transparent 0,
                    transparent 2ch) 0 100%/12ch 3px no-repeat;
            """.trimIndent())
            value(value.data)
            changes.values().map(String::uppercase) handledBy value.update
            maxLength(6)
            placeholder("")
        }
    }
}